'use client'

import { useEffect, useRef, useState } from 'react'
import { trackClarityEvent } from '@/lib/clarity'

interface TermosCondicoesModalProps {
  isOpen: boolean
  onClose: () => void
  titulo: string
  conteudo: string
  onReadComplete?: () => void
}

export default function TermosCondicoesModal({
  isOpen,
  onClose,
  titulo,
  conteudo,
  onReadComplete,
}: TermosCondicoesModalProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setHasScrolledToBottom(false)
      return
    }

    trackClarityEvent(`termos_modal_aberto: ${titulo.substring(0, 50)}`)

    const checkScroll = () => {
      if (!contentRef.current) return

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px de tolerância

      if (isAtBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true)
        trackClarityEvent(`termos_modal_lidoate_final: ${titulo.substring(0, 50)}`)
        if (onReadComplete) {
          onReadComplete()
        }
      }
    }

    const contentElement = contentRef.current
    if (contentElement) {
      contentElement.addEventListener('scroll', checkScroll)
      // Verificar imediatamente se o conteúdo já está visível sem scroll
      checkScroll()

      return () => {
        contentElement.removeEventListener('scroll', checkScroll)
      }
    }
  }, [isOpen, hasScrolledToBottom, onReadComplete])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900">{titulo}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="Fechar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content - Scrollable */}
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto p-6 bg-gray-50"
          style={{ maxHeight: 'calc(90vh - 140px)' }}
        >
          <div
            className="prose max-w-none text-black prose-headings:text-black prose-p:text-black prose-strong:text-black prose-ul:text-black prose-ol:text-black prose-li:text-black whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: conteudo.replace(/<p><\/p>/g, '<br>') }}
          />
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-4 bg-gray-50 rounded-b-2xl flex-shrink-0">
          {hasScrolledToBottom && (
            <div className="flex items-center gap-2 text-green-600 text-sm mr-auto">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="font-medium">Você leu todo o documento</span>
            </div>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

